import { IProduct } from "../../models/catalogue";
import { WebPageURL } from "../../models/nominals";
import { IAPIPrice } from "../../models/prices";
import { IChildFilter, ICompareTile } from "../../models/product-compare";
import { notEmpty } from "../../utils/functional";
import { sortProductsByPrice } from "../../utils/sorting";

/**
 * Flattens root products into a list of all their child variants.
 * If a root product has no children, includes the root product itself.
 * Returns variants sorted by price (ascending).
 */
const getFlattenedVariants = (
    rootProducts: ReadonlyArray<IProduct>,
): ReadonlyArray<IProduct> => {
    const variants = rootProducts.reduce<IProduct[]>((memo, rp) => {
        if (rp.children.length <= 0) {
            return memo.concat([rp]);
        }
        return memo.concat(rp.children);
    }, []);
    return sortProductsByPrice(variants);
};

/**
 * Checks if a product is available to buy.
 */
const isAvailableToBuy = (p: IProduct) => p.availability.is_available_to_buy;

/**
 * Selects the appropriate product variant for a comparison tile based on size selection
 * and featured product preferences.
 *
 * Selection logic:
 * 1. Start with child filter products if specified, otherwise use all root product variants
 * 2. Filter by selected size if provided
 * 3. If tile has both category_id and product_id, prefer variants from the "featured" product
 * 4. Prioritize available-to-buy variants
 * 5. Return cheapest variant (by price) that matches criteria
 *
 * @param tile - The comparison tile containing product data
 * @param selectedSize - The size filter selected by user (e.g., "Queen")
 * @returns The selected product variant, or null if none found
 */
export const getSelectedVariant = (
    tile: ICompareTile,
    selectedSize: string | null,
): IProduct | null => {
    // If the tile is set to only use a subset of variants, start with those. Otherwise,
    // use a flattened list of all of the variants for all of the root products.
    let possibleVariants =
        tile.childFilterProducts || getFlattenedVariants(tile.rootProducts);
    // Filter the possible variant list by the selected size.
    if (selectedSize !== null) {
        possibleVariants = possibleVariants.filter((child) => {
            const attr = child.attributes.option_size;
            return !!attr && attr.value === selectedSize;
        });
    }
    // If a both a categoryID and a productID are set, the productID is the "featured"
    // product. This is the product that must be used for pricing data. So, limit the
    // variant options to its children. BUT, if the "featured" isn't available to buy,
    // use the cheapest variant instead.
    if (
        tile.product_compare_tile.category_id &&
        tile.product_compare_tile.product_id
    ) {
        const featuredRoot = tile.rootProducts.find(
            (p) => p.id === tile.product_compare_tile.product_id,
        );
        if (featuredRoot) {
            const featuredRootAvailableChildren =
                featuredRoot.children.filter(isAvailableToBuy);
            const featuredRootAvailableChildrenIDs = new Set(
                featuredRootAvailableChildren.map((p) => p.id),
            );
            const possibleVariantsOfFeaturedRoot = possibleVariants.filter(
                (v) => featuredRootAvailableChildrenIDs.has(v.id),
            );
            if (possibleVariantsOfFeaturedRoot.length > 0) {
                possibleVariants = possibleVariantsOfFeaturedRoot;
            }
        }
    }
    // Filter the possible variant list down to only what's available to buy
    const availablePossibleVariants = possibleVariants.filter(isAvailableToBuy);
    // Return the cheapest available-to-buy variant if one exists, else the cheapest unavailable-to-buy product.
    return availablePossibleVariants[0] || possibleVariants[0] || null;
};

/**
 * Determines the appropriate "Shop Now" URL for a comparison tile.
 *
 * Priority order:
 * 1. Category page link (if tile has category_id and category page exists)
 * 2. Selected variant's PDP link
 * 3. First root product's link
 * 4. null if no link is available
 *
 * @param tile - The comparison tile
 * @param selectedVariant - The currently selected product variant
 * @returns The URL to navigate to when user clicks "Shop Now"
 */
export const getShopNowURL = (
    tile: ICompareTile,
    selectedVariant: IProduct | null,
): WebPageURL | null => {
    // If this is a category tile, try to specifically use a category page, even if a PDP exists.
    const categoryPage = (tile.rootProducts.at(0)?.alternate_links || []).find(
        (link) =>
            link.type === "ProductLinePage" ||
            link.type === "ProductLineCollectionPage" ||
            link.type === "GridPage",
    );
    const url =
        (tile.product_compare_tile.category_id !== null && categoryPage
            ? categoryPage.link
            : null) ||
        selectedVariant?.link ||
        tile.rootProducts.at(0)?.link ||
        null;
    return url;
};

/**
 * Filters product variants based on a child filter configuration.
 *
 * Supports two filter behaviors:
 * - "inclusive": Returns only variants that match the specified filter criteria
 * - "exclusive": Returns only variants that do NOT match the specified filter criteria
 *
 * @param tile - The comparison tile containing products
 * @param childFilter - The filter configuration specifying behavior and criteria
 * @returns Array of filtered product variants, or null if filter behavior is invalid
 */
export const getChildFilterVariants = (
    tile: ICompareTile,
    childFilter: IChildFilter,
) => {
    if (childFilter.behavior === "inclusive") {
        const variants = childFilter.variants
            .map((variant) => {
                return getFlattenedVariants(tile.rootProducts).filter(
                    (child) => {
                        const option_group = childFilter.option_group.code;
                        const attr = child.attributes[option_group];
                        if (childFilter.behavior === "inclusive") {
                            if (attr && attr.value === variant.name) {
                                return child;
                            }
                        }
                        return null;
                    },
                );
            })
            .filter(notEmpty);

        // flatten variants array
        return variants.reduce((array, el) => {
            return array.concat(el);
        }, []);
    }
    if (childFilter.behavior === "exclusive") {
        return getFlattenedVariants(tile.rootProducts).filter((child) => {
            const option_group = childFilter.option_group.code;
            const attr = child.attributes[option_group];

            let isExcluded = false;
            childFilter.variants.map((variant) => {
                if (attr && attr.value === variant.name) {
                    isExcluded = true;
                }
            });

            if (isExcluded) {
                return null;
            }

            return child;
        });
    }
    return null;
};

/**
 * Constructs an API price object for a product variant.
 *
 * @param variant - The product variant to get pricing for
 * @returns Price object formatted for API consumption
 */
export const getVariantPrice = (variant: IProduct): IAPIPrice => {
    const price: IAPIPrice = {
        product: variant.url,
        quantity: 1,
        total: variant.price,
        unit: variant.price,
    };
    return price;
};
